import PropTypes from 'prop-types';
import React from 'react';
import isEmpty from 'lodash.isempty';
import eurekaMgrs from '@eureka/ui-managers';
import { ConfigContext } from '../context';
import I18nProvider from '../i18n-provider';
import { loadAssets } from '../utils';

const { ConfigManager } = eurekaMgrs;
const { i18n, initI18n } = I18nProvider;
const LOCAL_BASE_URL = 'http://localhost';

/**
 * @description Get i18n configuration with fragment data
 * @param fragments {arr}  Array of fragment configs in config.json
 * @return [i18nNamespaces, i18BackendConfig]
 */
export function i18nConfigFromFragments(appName, fragments) {
  if (!appName) {
    return [[], {}];
  }

  const defaultNs = [appName];
  const defaultConfig = {
    [appName]: `${LOCAL_BASE_URL}:${window.location.port}`,
  };

  if (!Array.isArray(fragments)) {
    return [defaultNs, defaultConfig];
  }

  return fragments.reduce(
    (acc, fragment) => {
      const [accNs, accConfig] = acc;
      const {
        port: fragmentPort,
        config: { app: fragmentName },
      } = fragment;

      return [
        [...accNs, fragmentName],
        {
          ...accConfig,
          [fragmentName]: `${LOCAL_BASE_URL}:${fragmentPort}`,
        },
      ];
    },
    [defaultNs, defaultConfig],
  );
}

/**
 * @description A common provider for JSFirestarter MicroFrontends and Fragment applications.
 * - Provides hook access to config object (config.json)
 * - Development Only:
 *   - Initializes i18n
 *   - Requests fragment.json file(partial data used in config.json for fragments) and sets as config object
 *   - Requests feature-toggles.json and sets feature toggle config
 *   - Loads fragment's i18n namespaces
 *   - Requests and sets csrf token
 */
export default class CommonProvider extends React.Component {
  static propTypes = {
    appName: PropTypes.string, // ex. process.env.REACT_APP_NAME
    children: PropTypes.node,
    config: PropTypes.object,
    isDevelopment: PropTypes.bool, // ex. process.env.NODE_ENV !== 'production'
    renderLoading: PropTypes.func,
  };

  constructor(props) {
    super(props);

    this.state = {
      config: props.config,
      loading: true,
    };
  }

  componentDidMount() {
    const { config, isDevelopment } = this.props;

    // NOTE: Used to avoid errors with setState
    // after component is unmounted during tests
    this._isMounted = true;

    // Load local features only if in development or not running through the shell
    if (isDevelopment && isEmpty(config)) {
      this.loadFragments()
        .then(this.loadFeatureToggles)
        .then(this.initializeI18nForDev)
        .then(this.fetchToken)
        .then(this.clearLoadingState);
    } else {
      this.loadMissingI18nNamespace().then(this.clearLoadingState);
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  clearLoadingState = () => {
    if (this._isMounted) {
      this.setState({ loading: false });
    }
  };

  initializeI18nForDev = fragments => {
    const { appName } = this.props;
    const [i18nNamespaces, i18BackendConfig] = i18nConfigFromFragments(appName, fragments);

    // NOTE: Previously added in public/index.html
    return initI18n(i18BackendConfig, {
      debug: true,
      defaultNS: i18nNamespaces[0],
      fallback: false,
      fallbackLng: false,
      lowerCaseLng: false,
      ns: i18nNamespaces,
    });
  };

  loadMissingI18nNamespace = () => {
    const { appName: ns } = this.props;

    if (i18n.hasLoadedNamespace(ns)) {
      return Promise.resolve();
    }

    return i18n.loadNamespaces([ns]).then(() => {
      console.log(`loadNamespace --> ${ns}`);
    });
  };

  /**
   * @description Load feature-toggles.json file for feature testing
   */
  loadFeatureToggles = () => {
    return fetch('/feature-toggles.json')
      .then(res => {
        const contentType = res.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          return res.json();
        }
      })
      .then(data => {
        if (data && data.resultList) {
          ConfigManager.updateFeatureToggle(data.resultList);
        }
      })
      .catch(() => {
        // NOTE: Only for development, and current App may not have feature toggles
      });
  };

  loadFragments = () => {
    return fetch('/fragments.json')
      .then(res => {
        const contentType = res.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          return res.json();
        }
      })
      .then(fragments => {
        if (fragments) {
          console.log(`Fragments loaded:`, fragments);
          fragments.forEach(({ config, port } = {}) => {
            const host = `${LOCAL_BASE_URL}:${port}`;
            if (
              config &&
              config.fragments &&
              Array.isArray(config.fragments) &&
              config.fragments.length > 0
            ) {
              loadAssets(host, config.app, 'fragment');
            }
          });

          if (this._isMounted) {
            this.setState({
              config: {
                components: fragments,
              },
            });
          }

          return Promise.resolve(fragments);
        }
      })
      .catch(err => {
        // NOTE: Only for development, and current App may not have fragments
      });
  };

  fetchToken = () => {
    return fetch(`/auth/csrf-token`, {
      method: 'POST',
      mode: 'cors',
      cache: 'no-cache',
      withCredentials: true,
      redirect: 'follow',
      referrerPolicy: 'no-referrer',
    })
      .then(res => {
        if (res.ok) {
          return res.json();
        } else {
          throw new Error(`${res.statusText} ${res.status}`);
        }
      })
      .then(res => {
        if (res && res.token) {
          ConfigManager.setCsrfToken(res.token);
        }
      })
      .catch(err => {
        // NOTE: If /auth/csrf-token route exists, and returns 401
        // this is ignored, and redirected back to the /login page
        ConfigManager.setCsrfToken(`fakecsrftoken`);
      });
  };

  renderChildren = () => {
    const { children, renderLoading } = this.props;
    const { loading } = this.state;

    if (loading) {
      return typeof renderLoading === 'function' ? renderLoading() : null;
    }

    return children;
  };

  // NOTE: Possibly handle other aspects of MicroFrontend Apps
  // - other providers such as SettingsContext/UserContext
  render() {
    const { config } = this.state;

    return <ConfigContext.Provider value={config}>{this.renderChildren()}</ConfigContext.Provider>;
  }
}
